import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {STANDARD_MAPPING_FIELDS, REQUIRED_MAPPINGS} from './types';
import type {ColumnMapping} from './types';

type MappingStepProps = {
  fileName: string | undefined;
  totalRows: number;
  csvHeaders: string[];
  mappings: ColumnMapping[];
  sampleRows: string[][];
  onUpdateMapping: (index: number, systemField: string) => void;
  canProceed: boolean;
  onBack: () => void;
  onContinue: () => void;
};

export function MappingStep({
  fileName,
  totalRows,
  csvHeaders,
  mappings,
  sampleRows,
  onUpdateMapping,
  canProceed,
  onBack,
  onContinue,
}: MappingStepProps) {
  return (
    <Card className='border-0 shadow-lg bg-white overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Step 4: Column Mapping
          </CardTitle>
          <div className='flex items-center gap-2'>
            <Badge className='bg-brand-blue/10 text-brand-blue border-brand-blue/20'>
              {fileName}
            </Badge>
            <Badge className='bg-gray-100 text-gray-700 border-gray-200'>
              {totalRows} rows
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className='p-6'>
        <div className='text-sm text-gray-600 mb-4'>
          Map each CSV column to a member field. Required fields:{' '}
          {REQUIRED_MAPPINGS.map((f) => (
            <Badge
              key={f}
              className={`mx-0.5 ${
                mappings.some((m) => m.systemField === f)
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              {f}
            </Badge>
          ))}
        </div>

        <div className='space-y-3'>
          <div className='grid grid-cols-[1fr_auto_1fr_1fr] gap-4 px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-600'>
            <span>CSV Column</span>
            <span />
            <span>Map To</span>
            <span>Sample Values</span>
          </div>
          {csvHeaders.map((header, index) => (
            <div
              key={header}
              className='grid grid-cols-[1fr_auto_1fr_1fr] gap-4 px-3 py-2 items-center border-b border-gray-50'
            >
              <span className='text-sm font-medium text-brand-navy'>{header}</span>
              <span className='text-gray-400'>→</span>
              <Select
                value={mappings[index]?.systemField || 'skip'}
                onValueChange={(v) => onUpdateMapping(index, v === 'skip' ? '' : v)}
              >
                <SelectTrigger className='h-9'>
                  <SelectValue placeholder='Skip' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='skip'>— Skip —</SelectItem>
                  {STANDARD_MAPPING_FIELDS.map((field) => (
                    <SelectItem key={field} value={field}>
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className='text-xs text-gray-500 truncate'>
                {sampleRows
                  .map((row) => row[index] || '')
                  .filter(Boolean)
                  .slice(0, 2)
                  .join(', ')}
              </span>
            </div>
          ))}
        </div>

        <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-100'>
          <Button variant='outline' onClick={onBack}>
            Back
          </Button>
          <div className='flex items-center gap-3'>
            {!canProceed && (
              <span className='text-sm text-red-600'>
                Map all required fields to continue
              </span>
            )}
            <Button onClick={onContinue} disabled={!canProceed}>
              Continue
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
